import { DatePipe } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { CommonUtil } from 'src/app/core/Utils/common-util';
import { HaloLoaderService } from 'src/app/core/halo-loader/halo-loader.service';
import { HaloModalSize } from 'src/app/core/halo-modal/halo-modal-interface';
import { HaloModalService } from 'src/app/core/halo-modal/halo-modal.service';
import { EToastType, HaloToastService } from 'src/app/core/halo-toast/halo-toast.service';
import { AmplitudeAnalyticsService } from 'src/app/core/services/amplitude-analytics.service';
import { SelectionInfo } from 'src/app/post-login/selectors/multi-selection/multi-selection.model';
import { ProductSettingsService } from '../product-settings.service';
import { AssetMgrConfigPeakTime, WorkoutProductSettings, WorkoutSettingQuickValue } from '../setting.model';

@Component({
  selector: 'app-workout-product-settings',
  templateUrl: './workout-product-settings.component.html',
  styleUrls: ['./workout-product-settings.component.scss']
})
export class WorkoutProductSettingsComponent implements OnInit, OnChanges {
  private hasEditPermission = true;
  private hasNewItem = false;
  private saveAsError = false;
  private originalData: WorkoutProductSettings[];
  private settingsData: WorkoutProductSettings[];
  private selectedSetting: WorkoutProductSettings | undefined = undefined;
  private showDeleteMsg = false;
  private saveAsConfigName: string;
  private NEW_SETTING_ID = -2;
  private isIntegrityXsds: boolean;
  private isIntegrityDsl: boolean;
  private isIntegrityDslSpeed: boolean;
  private hours: number[] = [];
  private minutes: number[] = [];
  minIncline = 0;
  showError = false;
  private multiBaseSupportConsoles = ['hercules', 'batman', 'robin'];
  private deleteModalProps = {
              title: this.commonUtil.replaceValueAsHTMLOnTranslatedText('DELETE.NO', [this.commonUtil.translateText('DRAFT.CONFIRMATION.SETTING')]),
              message: 'DELETE.MODAL.MESSAGE',
              isWarningPopup: true,
              ctaButtonLabel: 'DELETE',
              negativeButtonLabel: 'CANCEL',
              isCloseIconCallsCta: false,
              isSecondaryTextButton: true,
              hideSecondaryButton: false,
            };
  
  @Input() selectionInfo: SelectionInfo;
  @Input() isSelectionChanged: boolean;

  @ViewChild('saveAsModalTemplate') saveAsModalTemplate: TemplateRef<any>;
  @ViewChild('saveAsModalActionTemplate') saveAsModalActionTemplate: TemplateRef<any>;

  constructor(
    private settingsService: ProductSettingsService,
    private modal: HaloModalService,
    private commonUtil: CommonUtil,
    private loader: HaloLoaderService,
    private toast: HaloToastService,
    private _amplitudeAnalytics: AmplitudeAnalyticsService
  ) { }

  ngOnInit(): void {
    this.prepareTimeArrays();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isSelectionChanged?.currentValue){
      this.hasEditPermission = this.selectionInfo.facility.summary?.isLoggedInManagerAdmin === 'true' ||
      this.selectionInfo.facility.summary?.userRights.customize === 'F';
      this.isIntegrityXsds = this.selectionInfo.consoleType=='hercules' && 
                            this.selectionInfo.facility.summary.herculesTread==2 && 
                            this.selectionInfo.activityId=='1';
      this.isIntegrityDsl = (this.selectionInfo.consoleType=='batman' && 
                            this.selectionInfo.facility.summary.batmanTread==2 && 
                            this.selectionInfo.activityId=='1') || 
                            (this.selectionInfo.consoleType=='robin' && 
                            this.selectionInfo.facility.summary.robinTread==2 && 
                            this.selectionInfo.activityId=='1');
      this.isIntegrityDslSpeed = (this.selectionInfo.consoleType=='hercules' && 
                            this.selectionInfo.facility.summary.herculesTread==0 && 
                            this.selectionInfo.activityId=='1') ||
                            (this.selectionInfo.consoleType=='batman' && 
                            this.selectionInfo.facility.summary.batmanTread==0 && 
                            this.selectionInfo.activityId=='1') || 
                            (this.selectionInfo.consoleType=='robin' && 
                            this.selectionInfo.facility.summary.robinTread==0 && 
                            this.selectionInfo.activityId=='1');
      this.getData();
      this.hasNewItem = false;
    }
  }
  
  private prepareTimeArrays() {
    for (let index = 0; index < 60; index++) {
      if(index > 0 && index < 13) {
        this.hours.push(index);
      }
      this.minutes.push(index);
    }
  }

  private async getData() {
    if(this.selectionInfo.consoleType && this.selectionInfo.facility) {
      try {
        this.loader.startProcess();
        this.selectedSetting = undefined;
        this.originalData = await this.settingsService.getWorkoutProductSettings([this.selectionInfo.facility.facilityId], this.selectionInfo.consoleType, this.selectionInfo.activityId) as any;
        if(this.originalData) {
          this.originalData.forEach(setting => this.postProcessSetting(setting));
          this.settingsData = JSON.parse(JSON.stringify(this.originalData)); // deep copy
          this.settingsData.forEach(item => {
            if(item.peakTimeList && item.peakTimeList.length > 0) {
              item.peakTimeList.forEach(peakItem => {
                if(peakItem.tempData === undefined) {
                  peakItem.tempData = {};
                }
                peakItem.tempData.startHour = this.mapHoursFrom24to12(peakItem.tempData?.startHour);
                peakItem.tempData.endHour = this.mapHoursFrom24to12(peakItem.tempData?.endHour);
              });
            }
          });
        }else{
          this.settingsData = [];
        }
        this.selectionInfo.rootScopeData.isPageHasUnsavedChanges = false;
      } catch (error) {
        console.error('API error.');
      }
      finally {
        this.loader.endProcess();
        this.commonUtil.scrollTop();
      }
    }
  }

  private postProcessSetting(setting: WorkoutProductSettings) {
    if(!setting?.tempData) return;
    if((this.selectionInfo.consoleType=="hercules" && (this.selectionInfo.facility.summary.herculesTread== 0 || this.selectionInfo.facility.summary.herculesTread == 2)) || 
      (this.selectionInfo.consoleType=="batman" && (this.selectionInfo.facility.summary.batmanTread == 0 || this.selectionInfo.facility.summary.batmanTread == 2)) || 
      (this.selectionInfo.consoleType=="robin" && (this.selectionInfo.facility.summary.robinTread == 0 || this.selectionInfo.facility.summary.robinTread == 2))) {
        setting.tempData.maxSpeedForNewModel = setting.displayUnits==1?19:12;
    }
    if(this.selectionInfo.consoleType == 'superman' && 
      (this.selectionInfo.facility.summary.robinTread == 2 || this.selectionInfo.facility.summary.robinTread == 1)) {
        setting.tempData.maxSpeedForNewModel = setting.displayUnits==1?29:18;
    }
    if(setting.updatedAt) {
      setting.updatedAtTimeStamp = new Date(setting.updatedAt).getTime();
    }
    if(setting.peakTimeList && setting.peakTimeList.length > 0) {
      if(setting.peakTimeList[1].tempData === undefined) {
        setting.peakTimeList[1].tempData = {};
      }
      setting.peakTimeList[1].tempData.isEnabled = !(Number(setting.peakTimeList[1].startTime) === 0 && Number(setting.peakTimeList[1].duration) === 0); 
    }
    if(setting.quickResistanceValues && setting.quickResistanceValues.filter(x => !x.quickValue).length === 6) {
      if(setting.tempData) {
        setting.quickResistanceValues.forEach(q => q.hasError=true); 
        setting.tempData.hasQuickResistanceValueError = true;
        setting.tempData.quickResistanceValueError = this.commonUtil.translateText('ATLEAST.TWO.SETTINGS.ERROR.MESSAGE');
        this.setErrorFlag(setting);   
      }
    }
  }

  private onToggleCollapse(event: any, setting: WorkoutProductSettings) {
    this.selectedSetting = setting;
    const expanded = event.target.getAttribute('aria-expanded');
    if(expanded === 'true'){
      this.selectedSetting = undefined;
    }
  }
  
  private addNew() {
    if(this.hasNewItem){
      return;
    }
    const newProductSetting = this.getNewProductSetting();
    if(newProductSetting){
      this.selectedSetting = newProductSetting;
      if(newProductSetting && newProductSetting.peakTimeList && newProductSetting.peakTimeList.length > 0) {
        newProductSetting.peakTimeList.forEach((item: AssetMgrConfigPeakTime) => {
          if(item.tempData === undefined) {
            item.tempData = {};
          }
          item.tempData.startHour = this.mapHoursFrom24to12(item.tempData?.startHour);
          item.tempData.endHour = this.mapHoursFrom24to12(item.tempData?.endHour);
        });
      }
      this.settingsData.unshift(newProductSetting);
      this.showSelectedSetting();
    }

    this.hasNewItem = true;
    this.selectionInfo.rootScopeData.isPageHasUnsavedChanges = true;
  }

  mapHoursFrom24to12(hours: any) {
    return hours !== undefined ? hours > 12 ? hours - 12 : hours : undefined;
  }

  quickInclineMin(index: number, settingId: number) {
    const originQuickInclineValues = this.getOriginQuickInclineValues(settingId);
    const currentData = this.settingsData.find(x=> x.id === settingId)?.quickInclineValues;
    if(originQuickInclineValues && currentData) {
      return index == 0 ? originQuickInclineValues[0] : currentData[index-1].quickValue ? Number(currentData[index-1].quickValue) + 1 : originQuickInclineValues[0].quickValue;
    }
    return
  }

  quickInclineMax(index: number, settingId: number) {
    const originQuickInclineValues = this.getOriginQuickInclineValues(settingId);
    if(originQuickInclineValues) {
      const currentData = this.settingsData.find(x=> x.id === settingId)?.quickInclineValues;
      if(currentData) {
        if(this.hasAllInclines(currentData)) {
          return index == currentData.length - 1 ? this.getMaxDefaultIncline() : currentData[index + 1].quickValue - 1;
        } else {
          let isLastIndex = index;
          for(let i = index + 1 ; i < currentData?.length; i++) {
            if(currentData[i].quickValue !== undefined) {
              isLastIndex = i;
              break;
            }
          }
          return isLastIndex === index ? Number(this.getMaxDefaultIncline()) - 1 : currentData[isLastIndex].quickValue - 1;
        }
      }

    }
    return '';
  }

  getOriginQuickInclineValues(settingId: number) {
    const originObj = settingId <= 0 ? 
    this.originalData.find(x => x.managerId === 0) :
    this.originalData.find(x => x.id === settingId);
    return originObj?.quickInclineValues;
  }

  getMaxDefaultIncline() {
    const defaultSetting = this.originalData.find(x => x.managerId === 0);
    if (this.selectionInfo.activityId === '4' && this.selectionInfo.consoleType === 'everest') {
      return 16;
    } else {
      return defaultSetting?.quickInclineValues[defaultSetting.quickInclineValues.length - 1].quickValue;
    }
  }

  isSE4AndArcTrainers(): boolean {
    const se4Consoles = ['hal','everest', 'falco', 'neo_se4'];

    const isSe4ConsoleItem = !!se4Consoles.find(console => console ===  this.selectionInfo.consoleType);
    const isArcTrainers = this.selectionInfo.activityId === '19';

    return isSe4ConsoleItem && isArcTrainers 
  }

  hasAllInclines(setting: any) {
    if(setting) {
      const undefinedValues = setting.filter((x:any) => x.quickValue == undefined);
      return undefinedValues.length == 0;
    }
    return true;
  }

  getNewProductSetting(){
    let newProductSetting;
    const defaultSetting = this.settingsData.find(s => s.managerId==0);
    if(defaultSetting) {
      const newSetting = JSON.parse(JSON.stringify(defaultSetting));
      delete newSetting['managerId'];
			delete newSetting['inUse'];
			delete newSetting['updatedAt'];
			delete newSetting['updatedAtTimeStamp'];

      const datePipe = new DatePipe('en-US');
      newSetting.configName = `${this.commonUtil.translateText('PRODUCT.SETTING')} ${datePipe.transform(Date.now(), 'MMddyyyy_H:mm')}`;
      newSetting.id = this.NEW_SETTING_ID;
      newSetting.isEdited = true;
      newProductSetting = newSetting;
    }
    return newProductSetting;
  }

  
  private onEdit(setting: WorkoutProductSettings) {
    if(!setting?.tempData) return;
    setting.tempData.isEdited = true;
    this.selectionInfo.rootScopeData.isPageHasUnsavedChanges = true;
  }

  private undo(setting: WorkoutProductSettings, index: number) {
    if(setting.id !== -2){
      let oldSetting = this.originalData.find(s => s.id == setting.id);
      if (oldSetting) {
        oldSetting = {
          ...oldSetting,
          tempData: {
            ...oldSetting.tempData,
            ...this.settingsData[index].tempData
          }
        };
      } 
      this.settingsData[index] = JSON.parse(JSON.stringify(oldSetting)); // assign back the original data
    }else{
      const newProductSetting = this.getNewProductSetting();
      if(newProductSetting){
        this.settingsData[index] = JSON.parse(JSON.stringify(newProductSetting)); // assign back the original data
      }
    }
   
    this.selectedSetting = this.settingsData[index];
    this.showSelectedSetting();
    this.checkUnsaveData();
    this.resetAllErrorFlag(this.settingsData[index]);
  }

  private showSaveAs() {
    if(!this.selectedSetting || this.selectedSetting.quickValues?.filter(val => val.hasError).length>0) return;
    this.hasNewItem = false;
    this.saveAsError = false;
    this.showSaveAsModalDialog();
  }

  showSaveAsModalDialog(){
    const datePipe = new DatePipe('en-US');
    this.saveAsConfigName = `${this.commonUtil.translateText('PRODUCT.SETTING')} ${datePipe.transform(Date.now(), 'MMddyyyy_H:mm')}`;
    this.modal.add({
      dismissible: true,
      titleUppercase: true,      
      modalSize: HaloModalSize.Medium,
      title: this.commonUtil.translateText('SAVEAS'),
      bodyTemplate: this.saveAsModalTemplate,
      footerTemplate: this.saveAsModalActionTemplate
    });
  }

  private closeSaveAs() {
    this.modal.close();
  }

  private saveAsSetting() {
    if(this.saveAsConfigName.trim() === ''){
      this.saveAsError = true;
    }else{
      if(!this.selectedSetting) return;
      this.saveAsError = false;
      let saveAsData = JSON.parse(JSON.stringify(this.selectedSetting)) as WorkoutProductSettings;
      saveAsData.id = this.NEW_SETTING_ID;
      saveAsData.configName = this.saveAsConfigName;
      let indexToSave = 0;
      // check for draft item
      if(this.settingsData[0].id == this.NEW_SETTING_ID) {
        indexToSave = 1; // insert to second index
      }
      saveAsData = {
        ...saveAsData,
        tempData: {
          ...saveAsData.tempData,
          ...this.settingsData[indexToSave].tempData
        }
      }
      this.settingsData.splice(indexToSave, 0, saveAsData); 
      this.save(saveAsData, indexToSave);
      this.closeSaveAs();
    } 
  }

  private async save(setting: WorkoutProductSettings, index: number) {
    if(!setting || setting.quickValues?.filter(val => val.hasError).length>0) return;
    const settingToSave = this.prepareDataToSave(setting);

    try {
      let isPeakEnaabledObj = this.settingsData.find(x => x.id === settingToSave.id); 
      if(isPeakEnaabledObj === undefined) {
        isPeakEnaabledObj = settingToSave;
      }
      if(isPeakEnaabledObj?.isPeakTimeEnabled && isPeakEnaabledObj?.peakTimeList && isPeakEnaabledObj?.peakTimeList.length > 0){
        if(setting && setting?.peakTimeList[1].tempData && !setting.peakTimeList[1].tempData.isEnabled) {
          settingToSave.peakTimeList[1].startTime = 0;
          settingToSave.peakTimeList[1].duration = 0;
        } else {
          settingToSave.peakTimeList[1].startTime = this.toMinutes(setting?.peakTimeList[1].tempData?.startHour, setting?.peakTimeList[1].tempData?.startMinute, setting?.peakTimeList[1].tempData?.startUnit === 'AM');
          const end = this.toMinutes(setting?.peakTimeList[1].tempData?.endHour, setting?.peakTimeList[1].tempData?.endMinute, setting?.peakTimeList[1].tempData?.endUnit === 'AM');
          settingToSave.peakTimeList[1].duration = end - settingToSave.peakTimeList[1].startTime;
    
        }
      }
      this.loader.startProcess();
      const response = await this.settingsService.saveWorkoutProductSettings(settingToSave);
      this.selectionInfo.rootScopeData.isPageHasUnsavedChanges = false;
      if(response && response.peakTimeList && response.peakTimeList.length > 0){
        response.peakTimeList.forEach((peakItem: AssetMgrConfigPeakTime) => {
          if(peakItem.tempData === undefined) {
            peakItem.tempData = {};
          }
          peakItem.tempData.startHour = this.mapHoursFrom24to12(peakItem.tempData?.startHour);
          peakItem.tempData.endHour = this.mapHoursFrom24to12(peakItem.tempData?.endHour);
        });
      }
      response.displayUnits = setting.displayUnits;
      this.postProcessSetting(response);
      const copy = JSON.parse(JSON.stringify(response));
      // for new data, insert at the start of originalData
      if(setting.id == this.NEW_SETTING_ID) {
        this.originalData.unshift(copy);
      } else { //replace the old setting
        const idx = this.originalData.findIndex(s => s.id == setting.id)
        if(idx > -1) this.originalData[idx] = copy;
      } 
      
      this.settingsData[index] = {
        ...response,
        tempData: {
          ...this.settingsData[index].tempData,
          ...response.tempData
        }
      };
      this.selectedSetting = response;
      this.hasNewItem = false;
      this.toast.creatToast(this.commonUtil.replaceValueAsHTMLOnTranslatedText('GENERIC.SAVED.SUCCESSFULLY', ['<b>'+settingToSave.configName+'</b>']), EToastType.SUCCESS);
      this.showSelectedSetting();

    } catch (error) {
      console.error("API error.")
    }
    finally {
      this.loader.endProcess();
    }
  }

  private async publish(setting: WorkoutProductSettings) {
    if(!setting) return;
    const settingToSave = this.prepareDataToSave(setting);
    const settingToPublish = this.prepareDataToPublish(settingToSave, setting);
    if(settingToPublish.managerId == 0) settingToPublish.id = -2;
    const postData = {
      workoutSetting: settingToPublish,
      consoleType: this.selectionInfo.consoleType,
      facilityIds: [this.selectionInfo.facility.facilityId],
      activityIds: [this.selectionInfo.activityId]
    }

    try {
      this.loader.startProcess();
      await this.settingsService.publishWorkoutProductSettings(postData);
      this.selectionInfo.rootScopeData.isPageHasUnsavedChanges = false;
      this.toast.creatToast(this.commonUtil.replaceValueAsHTMLOnTranslatedText('GENERIC.PUBLISHED.SUCCESSFULLY', ['<b>'+settingToPublish.configName+'</b>']), EToastType.SUCCESS);
      this.getData();
    } catch (error) {
      console.error("API error.")
    }
    finally {
      this.hasNewItem = false;
      this.loader.endProcess();
    }
  }

  private checkUnsaveData() {
    //@ts-ignore
    this.selectionInfo.rootScopeData.isPageHasUnsavedChanges = !!this.settingsData.find(s => s.tempData.isEdited);
  }

  private prepareDataToPublish(setting:any, rowSetting: any){
    const preparedData = setting;
    preparedData.activityId = Number(setting.activityId);
    preparedData.hybridMode = String(preparedData.hybridMode);
    preparedData.isFitTestEnabled = String(preparedData.isFitTestEnabled);
    preparedData.isMarathonModeEnabled = String(preparedData.isMarathonModeEnabled);
    let isPeakEnaabledObj = this.settingsData.find(x => x.id === preparedData.id); 
    if(isPeakEnaabledObj === undefined) {
      isPeakEnaabledObj = setting;
    }
    if(isPeakEnaabledObj?.isPeakTimeEnabled && isPeakEnaabledObj?.peakTimeList && isPeakEnaabledObj?.peakTimeList.length > 0 && setting?.peakTimeList && setting?.peakTimeList.length > 0){
      if(rowSetting && (!rowSetting?.peakTimeList[1].tempData?.isEnabled || rowSetting.isPeakTimeEnabled === false)) {
        preparedData.peakTimeList[1].startTime = 0;
        preparedData.peakTimeList[1].duration = 0;
      } else {
        if (setting.peakTimeList && setting.peakTimeList.length >=2) {
          const pt1StartHour = isPeakEnaabledObj?.peakTimeList[1].tempData?.startHour 
                                  ? this.toMinutes(isPeakEnaabledObj?.peakTimeList[1].tempData?.startHour, isPeakEnaabledObj?.peakTimeList[1].tempData?.startMinute, isPeakEnaabledObj?.peakTimeList[1].tempData?.startUnit === 'AM') 
                                  : isPeakEnaabledObj?.peakTimeList[1].startTime;
          const pt1StartMin = isPeakEnaabledObj?.peakTimeList[1].tempData?.endHour 
                                  ? this.toMinutes(isPeakEnaabledObj?.peakTimeList[1].tempData?.endHour, isPeakEnaabledObj?.peakTimeList[1].tempData?.endMinute, isPeakEnaabledObj?.peakTimeList[1].tempData?.endUnit === 'AM') - setting.peakTimeList[1].startTime
                                  : isPeakEnaabledObj?.peakTimeList[1].duration;
                                  preparedData.peakTimeList[1].startTime = pt1StartHour;
                                  preparedData.peakTimeList[1].duration = pt1StartMin;
        }
  
      }
    }   

    preparedData.isPeakTimeEnabled = String(preparedData.isPeakTimeEnabled);
    preparedData.isTelemetryEnabled = String(preparedData.isTelemetryEnabled);
    return preparedData;
  }

  private prepareDataToSave(setting: WorkoutProductSettings) {
    const settingToSave: any = {
      "id" : setting.id == this.NEW_SETTING_ID ? null : (setting.managerId === 0?this.NEW_SETTING_ID:setting.id),
      "activityId" : this.selectionInfo.activityId,
      "consoleType" : this.selectionInfo.consoleType,
      "configName" : setting.configName,
      "isMarathonModeEnabled" : setting.isMarathonModeEnabled,
      "isPeakTimeEnabled" : setting.isPeakTimeEnabled,
      "isTelemetryEnabled" : setting.isTelemetryEnabled,
      "maxWODurationPeak" : setting.maxWODurationPeak,
      "pauseTime" : setting.pauseTime,
      "programTimeout" : setting.programTimeout
    };

    if(setting.isPeakTimeEnabled == true) {
      settingToSave.peakTimeList = setting.peakTimeList.map(q => {return {'startTime': q.startTime, 'duration': q.duration}});
      settingToSave.maxWODurationOffPeak = setting.maxWODurationOffPeak;
    }
    if(this.selectionInfo.activityId != '18'){
      settingToSave.isFitTestEnabled = setting.isFitTestEnabled;
    }

    if ((this.selectionInfo.consoleType == 'neo' || this.selectionInfo.consoleType == 'batman') && this.selectionInfo.activityId == '1') {
      settingToSave.quickSpeedConfirmation = setting.quickSpeedConfirmation
    }

    if((this.selectionInfo.consoleType=='neo_se4' || this.selectionInfo.consoleType=="everest" || this.selectionInfo.consoleType=="hal" || this.selectionInfo.consoleType=="falco") && setting.quickInclineValues) {
      settingToSave.quickInclineValues = setting.quickInclineValues.filter(q => q.quickValue!=undefined || q.quickValueMt!=undefined).map(q => {return {'quickValue': q.quickValue}});
    }

    if(((this.selectionInfo.consoleType=='neo_se4' || this.selectionInfo.consoleType=='hal' || this.selectionInfo.consoleType=='falco') && this.selectionInfo.activityId == '19') && setting.quickResistanceValues) {
      settingToSave.quickResistanceValues = setting.quickResistanceValues.filter(q => q.quickValue!=undefined || q.quickValueMt!=undefined).map(q => {return {'quickValue': q.quickValue}});
    }

    if (this.selectionInfo.consoleType === 'everest' && this.selectionInfo.activityId === '2') {
      settingToSave.switchCycleValues = setting.switchCycleValues.filter(q => q.switchCycleValue != undefined);
      settingToSave.switchCycleValues = settingToSave.switchCycleValues.filter((q: any) => q.switchCycleValueMt != undefined);
    }
    
    if (this.selectionInfo.consoleType === 'everest' && (this.selectionInfo.activityId === '1' || this.selectionInfo.activityId === '4' || this.selectionInfo.activityId === '2')) {
      if (setting.displayUnits == 1) {
        if (this.selectionInfo.activityId === '2') {
          settingToSave.activeZoneValues = [
            ...setting.activeZoneLevelsClutchedValues.filter(q => q.activeZoneValueMt != undefined).map((item: any) => ({consoleSubType: item.consoleSubType, activeZoneType: item.activeZoneType, activeZoneValue: item.activeZoneValue ?? item.activeZoneValueMt, activeZoneValueMt: item.activeZoneValueMt })),
            ...setting.activeZoneLevelsClutchlessValues.filter(q => q.activeZoneValueMt != undefined).map((item: any) => ({consoleSubType: item.consoleSubType, activeZoneType: item.activeZoneType, activeZoneValue: item.activeZoneValue ?? item.activeZoneValueMt, activeZoneValueMt: item.activeZoneValueMt })),
          ]
        } else {
          settingToSave.activeZoneValues = [
            ...setting.activeZoneSpeedValues.filter(q => q.activeZoneValueMt != undefined).map(item => ({activeZoneType: item.activeZoneType, activeZoneValue: item.activeZoneValue ?? item.activeZoneValueMt, activeZoneValueMt: item.activeZoneValueMt })),
            ...setting.activeZoneLevelsValues.filter(q => q.activeZoneValueMt != undefined).map(item => ({activeZoneType: item.activeZoneType, activeZoneValue: item.activeZoneValue ?? item.activeZoneValueMt, activeZoneValueMt: item.activeZoneValueMt })),
            ...setting.activeZoneInclinesValues.filter(q => q.activeZoneValueMt != undefined).map(item => ({activeZoneType: item.activeZoneType, activeZoneValue: item.activeZoneValue ?? item.activeZoneValueMt, activeZoneValueMt: item.activeZoneValueMt })),
          ];
        }
      } else {
        if (this.selectionInfo.activityId === '2') {
          settingToSave.activeZoneValues = [
            ...setting.activeZoneLevelsClutchedValues.filter(q => q.activeZoneValue != undefined).map((item: any) => ({consoleSubType: item.consoleSubType, activeZoneType: item.activeZoneType, activeZoneValue: item.activeZoneValue, activeZoneValueMt: item.activeZoneValueMt ?? item.activeZoneValue })),
            ...setting.activeZoneLevelsClutchlessValues.filter(q => q.activeZoneValue != undefined).map((item: any) => ({consoleSubType: item.consoleSubType, activeZoneType: item.activeZoneType, activeZoneValue: item.activeZoneValue, activeZoneValueMt: item.activeZoneValueMt ?? item.activeZoneValue })),
          ];
        } else {
          settingToSave.activeZoneValues = [
            ...setting.activeZoneSpeedValues.filter(q => q.activeZoneValue != undefined).map(item => ({activeZoneType: item.activeZoneType, activeZoneValue: item.activeZoneValue, activeZoneValueMt: item.activeZoneValueMt ?? item.activeZoneValue })),
            ...setting.activeZoneLevelsValues.filter(q => q.activeZoneValue != undefined).map(item => ({activeZoneType: item.activeZoneType, activeZoneValue: item.activeZoneValue, activeZoneValueMt: item.activeZoneValueMt ?? item.activeZoneValue })),
            ...setting.activeZoneInclinesValues.filter(q => q.activeZoneValue != undefined).map(item => ({activeZoneType: item.activeZoneType, activeZoneValue: item.activeZoneValue, activeZoneValueMt: item.activeZoneValueMt ?? item.activeZoneValue }))
          ];
        }
      }
    }

    if ((this.selectionInfo.consoleType=='neo_se4' || this.selectionInfo.consoleType=="falco" || this.selectionInfo.consoleType=="everest" || this.selectionInfo.consoleType=="hal" || this.selectionInfo.consoleType == "aquaman" ||this.selectionInfo.consoleType == "superman") && setting.quickValues) {
      settingToSave.quickValues = setting.quickValues.filter(q => q.quickValue!=undefined || q.quickValueMt!=undefined).map(q => {return {'quickValue': q.quickValue, 'quickValueMt': q.quickValueMt}});
      settingToSave.quickValues = settingToSave.quickValues.map((item: any) => {
        if (setting.displayUnits === undefined || setting.displayUnits == 0 && item.quickValue) {
          return {'quickValue': item.quickValue};
        } else if (setting.displayUnits == 1 && item.quickValueMt) {
          return {'quickValueMt':  item.quickValueMt}
        }
        return null
      }).filter((item: any) => item);
    }
    
    if (this.selectionInfo.consoleType == 'superman' && this.selectionInfo.activityId == '28' && setting.quickValuesNewModel) {
      settingToSave.quickValuesNewModel = setting.quickValuesNewModel.filter(q => q.quickValue!=undefined || q.quickValueMt!=undefined).map(q => {return {'quickValue': q.quickValue, 'quickValueMt': q.quickValueMt}});
    }
    if((this.selectionInfo.activityId == '2' || this.selectionInfo.activityId == '3' || this.selectionInfo.activityId == '4'
      || this.selectionInfo.activityId == '15' || this.selectionInfo.activityId == '18' || this.selectionInfo.activityId == '19') && 
      (this.selectionInfo.consoleType=="neo_se4" || this.selectionInfo.consoleType=="everest" || this.selectionInfo.consoleType=="aquaman" || this.selectionInfo.consoleType == 'hal' || this.selectionInfo.consoleType == 'superman' || 
      this.selectionInfo.consoleType=="bluth" || this.selectionInfo.consoleType=="mulder" || this.selectionInfo.consoleType == 'falco')){
      settingToSave.hybridMode = setting.hybridMode;
    }

    if(this.selectionInfo.activityId != '1' && this.selectionInfo.activityId != '28' && this.selectionInfo.activityId != '18' && settingToSave.quickValues &&
      (this.selectionInfo.consoleType=="everest" || this.selectionInfo.consoleType=="aquaman" || this.selectionInfo.consoleType == 'hal' || this.selectionInfo.consoleType == 'superman' || 
      this.selectionInfo.consoleType=="bluth" || this.selectionInfo.consoleType=="mulder")){
      settingToSave.quickValues = setting.quickValues.filter(q => q.quickValue!=undefined || q.quickValueMt!=undefined).map(q => {return {'quickValue': q.quickValue, 'quickValueMt': q.quickValueMt}});
      settingToSave.quickValues = settingToSave.quickValues.map((item: any) => {
        if (setting.displayUnits === undefined || setting.displayUnits == 0 && item.quickValue) {
          return {'quickValue': item.quickValue};
        } else if (setting.displayUnits == 1 && item.quickValueMt) {
          return {'quickValueMt':  item.quickValueMt}
        }
        return null
      }).filter((item: any) => item);
    }
    if (this.selectionInfo.activityId=='1' || this.selectionInfo.activityId=='28' || this.selectionInfo.activityId === '4' || this.selectionInfo.activityId === '2') {
      settingToSave.isActivityZoneEnabled = setting.isActivityZoneEnabled;
    }

    if(this.selectionInfo.activityId=='1' || this.selectionInfo.activityId=='28'){
      settingToSave.displayUnits = setting.displayUnits;
      settingToSave.maxIncline = setting.maxIncline;
      settingToSave.isSpeedLimitUpKeyEnabled = setting.isSpeedLimitUpKeyEnabled;
      if(this.selectionInfo.activityId=='1' && setting.consoleType !== 'everest'){
        settingToSave.isStrideSensorEnabled = setting.isStrideSensorEnabled;
        settingToSave.isStrideSensorNotifPauseEnabled = setting.isStrideSensorNotifPauseEnabled;
      }else{
        settingToSave.minIncline = setting.minIncline;
        settingToSave.isCoachByColorEnabled = setting.isCoachByColorEnabled;
      }
      settingToSave.accelerationRate = setting.accelerationRate;
      settingToSave.decelerationRate = setting.decelerationRate;
      settingToSave.isPressAndHoldEnabled = setting.isPressAndHoldEnabled;
      if(setting.displayUnits == 0){
        settingToSave.maxSpeedEn = setting.maxSpeedEn;
        settingToSave.minSpeedEn = setting.minSpeedEn;
        settingToSave.maxSpeedMt = setting.maxSpeedMt;
        settingToSave.minSpeedMt = setting.minSpeedMt;
        if(this.selectionInfo.consoleType=="neo_se4" || this.selectionInfo.consoleType=='everest' || this.selectionInfo.consoleType=="aquaman" || this.selectionInfo.consoleType == 'hal' || this.selectionInfo.consoleType == 'superman' || 
          this.selectionInfo.consoleType=="bluth" || this.selectionInfo.consoleType=="mulder"){
          settingToSave.quickValues = setting.quickValues.filter(q => q.quickValue!=undefined || q.quickValueMt!=undefined).map(q => {return {'quickValue': q.quickValue, 'quickValueMt': q.quickValueMt}});
          settingToSave.quickValues = settingToSave.quickValues.map((item: any) => {
            if (setting.displayUnits == 0 && item.quickValue) {
              return {'quickValue': item.quickValue};
            } else if (setting.displayUnits == 1 && item.quickValueMt) {
              return {'quickValueMt':  item.quickValueMt}
            }
            return null
          }).filter((item: any) => item);
          if(setting.quickValuesNewModel && this.selectionInfo.consoleType == 'hal' || this.selectionInfo.consoleType == 'superman' && 
            this.selectionInfo.activityId=='28'){
            settingToSave.quickValuesNewModel = setting.quickValuesNewModel.filter(q => q.quickValue!=undefined || q.quickValueMt!=undefined).map(q => {return {'quickValue': q.quickValue, 'quickValueMt': q.quickValueMt}});
          }
        }
        if((this.selectionInfo.consoleType=="hercules" && (this.selectionInfo.facility.summary.herculesTread == 0 || this.selectionInfo.facility.summary.herculesTread == 2)) ||
          (this.selectionInfo.consoleType=="batman" && (this.selectionInfo.facility.summary.batmanTread == 0 || this.selectionInfo.facility.summary.batmanTread == 2)) || 
          (this.selectionInfo.consoleType=="robin" && (this.selectionInfo.facility.summary.robinTread == 0 || this.selectionInfo.facility.summary.robinTread == 2))){
          settingToSave.maxSpeedEnNewModel = setting.maxSpeedEnNewModel;
          settingToSave.maxSpeedMtNewModel = setting.maxSpeedMtNewModel;
          //settingToSave.maxInclineSX = setting.maxInclineSX;
        }
        if((this.selectionInfo.consoleType == 'hal' || this.selectionInfo.consoleType == 'superman' && this.selectionInfo.facility.summary.mylesTread == 1) || 
          (this.selectionInfo.consoleType == 'hal' || this.selectionInfo.consoleType == 'superman' && this.selectionInfo.facility.summary.mylesTread == 2)){
          settingToSave.maxSpeedEnNewModel = setting.maxSpeedEnNewModel;
          settingToSave.maxSpeedMtNewModel = setting.maxSpeedMtNewModel;
          settingToSave.minSpeedEnNewModel = setting.minSpeedEnNewModel;
          settingToSave.minSpeedMtNewModel = setting.minSpeedMtNewModel;
          //settingToSave.maxInclineSX = setting.maxInclineSX;
        }
      }else{
        settingToSave.maxSpeedEn = setting.maxSpeedEn;
        settingToSave.minSpeedEn = setting.minSpeedEn;
        settingToSave.maxSpeedMt = setting.maxSpeedMt;
        settingToSave.minSpeedMt = setting.minSpeedMt;
        if(this.selectionInfo.consoleType=="neo_se4" || this.selectionInfo.consoleType=='everest' || this.selectionInfo.consoleType=="aquaman" || this.selectionInfo.consoleType == 'hal' || this.selectionInfo.consoleType == 'superman' || 
          this.selectionInfo.consoleType=="bluth" || this.selectionInfo.consoleType=="mulder"){
          settingToSave.quickValues = setting.quickValues.filter(q => q.quickValue!=undefined || q.quickValueMt!=undefined).map(q => {return {'quickValue': q.quickValue, 'quickValueMt': q.quickValueMt}});
          settingToSave.quickValues = settingToSave.quickValues.map((item: any) => {
            if (setting.displayUnits == 0 && item.quickValue) {
              return {'quickValue': item.quickValue};
            } else if (setting.displayUnits == 1 && item.quickValueMt) {
              return {'quickValueMt':  item.quickValueMt}
            }
            return null
          }).filter((item: any) => item);
          if(setting.quickValuesNewModel && this.selectionInfo.consoleType == 'hal' || this.selectionInfo.consoleType == 'superman' && 
            this.selectionInfo.activityId=='28'){
            settingToSave.quickValuesNewModel = setting.quickValuesNewModel.filter(q => q.quickValue!=undefined || q.quickValueMt!=undefined).map(q => {return {'quickValue': q.quickValue, 'quickValueMt': q.quickValueMt}});
          }
        }
        if((this.selectionInfo.consoleType=="hercules" && (this.selectionInfo.facility.summary.herculesTread == 0 || 
          this.selectionInfo.facility.summary.herculesTread == 2)) || 
          (this.selectionInfo.consoleType=="batman" && (this.selectionInfo.facility.summary.batmanTread == 0 || this.selectionInfo.facility.summary.batmanTread == 2)) || 
          (this.selectionInfo.consoleType=="robin" && (this.selectionInfo.facility.summary.robinTread == 0 || this.selectionInfo.facility.summary.robinTread == 2))){
          settingToSave.maxSpeedEnNewModel = setting.maxSpeedEnNewModel;
          settingToSave.maxSpeedMtNewModel = setting.maxSpeedMtNewModel;
          //settingToSave.maxInclineSX = setting.maxInclineSX;
        }
        if((this.selectionInfo.consoleType == 'hal' || this.selectionInfo.consoleType == 'superman' && this.selectionInfo.facility.summary.mylesTread == 1) || 
          (this.selectionInfo.consoleType == 'hal' || this.selectionInfo.consoleType == 'superman' && this.selectionInfo.facility.summary.mylesTread == 2)){
          settingToSave.maxSpeedEnNewModel = setting.maxSpeedEnNewModel;
          settingToSave.maxSpeedMtNewModel = setting.maxSpeedMtNewModel;
          settingToSave.minSpeedEnNewModel = setting.minSpeedEnNewModel;
          settingToSave.minSpeedMtNewModel = setting.minSpeedMtNewModel;
          //settingToSave.maxInclineSX = setting.maxInclineSX;
        }
      }
      if(this.selectionInfo.consoleType=="duplo" || this.selectionInfo.consoleType=="t1"){
        delete settingToSave['isPressAndHoldEnabled'];
      }      
    }else if(this.selectionInfo.activityId=='16'){
      settingToSave.isWarmUpModeEnabled = setting.isWarmUpModeEnabled;
      settingToSave.isUpperBandEnabled = setting.isUpperBandEnabled;
    }
    if (setting.consoleType == 'everest') {
      settingToSave.isStrideSensorEnabled = true;
    }

    return settingToSave;
  }

  private toMinutes(hours?: number, minutes?: number, isAM?: boolean) {
    if(isAM && hours === 12) {
      hours = 0;
    }
    return (Number(hours) + (isAM || Number(hours) ===  12 ? 0 : 12)) * 60 + Number(minutes);
  }

  private showSelectedSetting() {
    if(!this.selectedSetting) return;
    setTimeout(() => {
      //@ts-ignore
      $(`#collapse${this.selectedSetting.id}`).collapse('show');
    }, 10);
  }

  private showDeleteModal() {
    if(!this.selectedSetting) return;
    this.showDeleteMsg = true;
    this.deleteModalProps.message = this.selectedSetting.inUse ? 
      this.commonUtil.replaceValueAsHTMLOnTranslatedText('DELETE.PUBLISHED.MODAL.MESSAGE', [this.commonUtil.translateText('DRAFT.CONFIRMATION.SETTING')]) :
      'DELETE.MODAL.MESSAGE';
  }

  private async confirmDelete() {
    if(!this.selectedSetting) return;
    if(this.selectedSetting.id !== this.NEW_SETTING_ID) {
      try {
        this.loader.startProcess();
        this._amplitudeAnalytics.logEvent({elementName: 'Delete', action: "Click"});
        await this.settingsService.deleteWorkoutProductSettings(this.selectedSetting.id as number, this.selectionInfo.consoleType);        
        // reload data when published setting is deleted
        if(this.selectedSetting.inUse){
          this.getData();
        } else {
          this.checkUnsaveData();          
        }
      } catch (error) {  
        console.error('API error.');
      }
      finally {
        this.loader.endProcess();
      }
    } 
    this.settingsData = this.settingsData.filter(s => s.id != this.selectedSetting?.id);
    this.selectedSetting = undefined;  
    this.showDeleteMsg = false;
    this.hasNewItem = false;
    this.commonUtil.scrollTop();
  }

  private onWorkoutDurationChange(setting: WorkoutProductSettings) {
    setting.maxWODurationPeak = setting.isPeakTimeEnabled ? 60 : 30;
    this.onEdit(setting);

    if(!setting.tempData) return;
    setting.tempData.maxWorkoutDurationError = false;
    this.setErrorFlag(setting);
    if(setting.isPeakTimeEnabled) {
      const offpeakMaxWorkoutDurationError = setting.tempData.offpeakMaxWorkoutDurationError;
      const peakTimer1Error = setting.peakTimeList[0].tempData?.hasError;
      const peakTimer2Error = setting.peakTimeList[1].tempData?.hasError;
      setting.tempData.offpeakMaxWorkoutDurationError = false;
      //@ts-ignore
      setting.peakTimeList[0].tempData?.hasError = false;
      //@ts-ignore
      setting.peakTimeList[1].tempData?.hasError = false;
      this.setErrorFlag(setting);
      setting.tempData.offpeakMaxWorkoutDurationError = offpeakMaxWorkoutDurationError;
      //@ts-ignore
      setting.peakTimeList[0].tempData?.hasError = peakTimer1Error;
      //@ts-ignore
      setting.peakTimeList[1].tempData?.hasError = peakTimer2Error;
    }    
    this.onEdit(setting);
  }

  private onPeakTimerChange(setting: WorkoutProductSettings, peakTime: AssetMgrConfigPeakTime) {
    if(!peakTime.tempData || !setting.tempData) return;
    peakTime.startTime = this.toMinutes(peakTime.tempData.startHour, peakTime.tempData.startMinute, peakTime.tempData.startUnit == 'AM');
    const endTime = this.toMinutes(peakTime.tempData.endHour, peakTime.tempData.endMinute, peakTime.tempData.endUnit == 'AM');
    peakTime.duration = endTime - peakTime.startTime;
    peakTime.tempData.hasError = peakTime.duration < 30 || peakTime.duration > 600;
    setting.tempData.hasError = peakTime.tempData.hasError || setting.tempData.hasError;
    this.onEdit(setting);
    this.setErrorFlag(setting);
  }

  private onPeakTimer2ToggleChange(setting: WorkoutProductSettings) {
    this.onEdit(setting);
    if(setting.peakTimeList[1].tempData?.isEnabled) {
      const peakTimer2Error = setting.peakTimeList[1].tempData?.hasError;
      //@ts-ignore
      setting.peakTimeList[1].tempData?.hasError = false;
      this.setErrorFlag(setting);
      //@ts-ignore
      setting.peakTimeList[1].tempData?.hasError = peakTimer2Error;
    } else {
      if(setting.peakTimeList[1].tempData) {
        const startTime = this.toMinutes(setting.peakTimeList[1].tempData?.startHour, setting.peakTimeList[1].tempData?.startMinute, setting.peakTimeList[1].tempData?.startUnit == 'AM');
        const endTime = this.toMinutes(setting.peakTimeList[1].tempData?.endHour, setting.peakTimeList[1].tempData?.endMinute, setting.peakTimeList[1].tempData?.endUnit == 'AM');
        const duration = endTime - startTime;
        const error = duration < 30 || duration > 600;
        setting.peakTimeList[1].tempData.hasError = error;    
        this.setErrorFlag(setting);
      }
    }
  }

  private onQuickSpeedChange(setting: WorkoutProductSettings, speed: WorkoutSettingQuickValue, isNewModel: boolean, min: number, max: number, isQuickInclineValues?: boolean, isQuickResistenceValues?: boolean, isSwitchCycleKeys? : boolean, isSpeedZoneValues?: boolean, isInclinesZoneValues?: boolean, isLevelZoneValues?: boolean, isClutchedValues?: boolean, isClutchlessValues?: boolean) {
    if(!setting.tempData) return;
    if (isQuickInclineValues) {
      speed.quickValueMt = speed.quickValue;
     setting.quickInclineValues.forEach(x => {
      if(x.quickValue) {
        x.quickValueMt = x.quickValue = x.quickValue;
      }
     });
    }
    if (isQuickResistenceValues) {
      speed.quickValueMt = speed.quickValue;
      setting.quickResistanceValues.forEach(x => {
        if(x.quickValue) {
          x.quickValueMt = x.quickValue = x.quickValue;
        }
       });
    }
    setting.tempData.isEdited = true;
    if (isQuickInclineValues) {
      setting.tempData.hasQuickInclinesValueError = false;
    } else if (isQuickResistenceValues) {
      setting.tempData.hasQuickResistanceValueError = false;
    } else if (isSwitchCycleKeys) {
      setting.tempData.hasSwitchCycleKeysValueError = false;
    } else if (isSpeedZoneValues) {
      setting.tempData.hasSpeedZoneValueError = false;
    } else if (isInclinesZoneValues) {
      setting.tempData.hasInclinesZoneValueError = false;
    } else if (isLevelZoneValues) {
      setting.tempData.hasLevelZoneValueError = false;
    } else if (isClutchedValues) {
      setting.tempData.hasLevelZoneClutchedError = false;
    } else if (isClutchlessValues) {
      setting.tempData.hasLevelZoneClutchlessError = false;
    } else if (!isQuickInclineValues && !isQuickResistenceValues && !isSwitchCycleKeys && !isSpeedZoneValues && !isInclinesZoneValues && !isLevelZoneValues && !isClutchedValues && !isClutchlessValues) {
      setting.tempData.hasQuickValueError = false;
    }
    if(isNewModel) {
      //@ts-ignore
      if(speed.quickValueMt == null) speed.quickValueMt = undefined;
      setting.quickValuesNewModel.forEach(q => q.hasError=false);
      setting.tempData.quickValueNMError = '';
      const filtered = setting.quickValuesNewModel.filter(t => t.quickValueMt != undefined);
      if(filtered.length < 2) {
        setting.quickValuesNewModel.forEach(q => q.hasError=true);
        setting.tempData.hasQuickValueError = true;
        this.setErrorFlag(setting);
        setting.tempData.quickValueNMError = this.commonUtil.translateText('ATLEAST.TWO.SETTINGS.ERROR.MESSAGE');
        return true;
      }

      const notAscendingIndex = this.getNotAscendingIndex(setting.quickValuesNewModel, isNewModel);
      // check if values are in ascending order
      if(notAscendingIndex != -1) {
        setting.quickValuesNewModel.filter((f,i) => i >= notAscendingIndex).filter(t => t.quickValueMt != undefined).forEach(q => q.hasError=true);
        setting.tempData.hasQuickValueError = true;
        this.setErrorFlag(setting);
        setting.tempData.quickValueNMError = this.commonUtil.translateText('ASCENDING.ORDER.ERROR.MESSAGE');
        return true;
      }

      const duplicates = filtered.filter(f => f.quickValueMt == speed.quickValueMt);
      const speedList = filtered.map(item => +item.quickValueMt);
      const size = new Set(speedList).size;

      // check for duplicates
      if(duplicates.length > 1 || size !== speedList.length) {
        duplicates.forEach(q => q.hasError=true);
        setting.tempData.hasQuickValueError = true;
        this.setErrorFlag(setting);
        setting.tempData.quickValueNMError = this.commonUtil.translateText('DUPLICATE.VALUE.ERROR.MESSAGE');
        return true;
      }

      if(speed.quickValueMt != undefined) {
        //@ts-ignore
        speed.hasError = speed.quickValueMt < min || speed.quickValueMt > max;
        if(speed.hasError) {
          setting.tempData.hasQuickValueError = true;
          this.setErrorFlag(setting);
          setting.tempData.quickValueNMError = this.commonUtil.translateText('MIN.MAX.RANGE.ERROR.MESSAGE');
          return true;
        }
      }
    }
    else {
      if (!isQuickInclineValues) {
        if(isQuickResistenceValues) {
          setting.tempData.quickResistanceValueError = '';
          setting.quickResistanceValues.forEach(q => q.hasError=false);
          //@ts-ignore
          if(setting.displayUnits == 1 && speed.quickValueMt == null) speed.quickValueMt = undefined;
          //@ts-ignore
          if(setting.displayUnits != 1 && speed.quickValue == null) speed.quickValue = undefined;
        } else if (isSwitchCycleKeys) {
          setting.tempData.switchCycleKeysValueError = '';
          setting.switchCycleValues.forEach(q => q.hasError = false);
          //@ts-ignore
          if(setting.displayUnits == 1 && speed.switchCycleValueMt == null) speed.switchCycleValueMt = undefined;
          //@ts-ignore
          if(setting.displayUnits != 1 && speed.switchCycleValue == null) speed.switchCycleValue = undefined;
        } else if (isSpeedZoneValues) {
          setting.tempData.speedZoneValueError = '';
          setting.activeZoneSpeedValues.forEach(q => q.hasError=false);
          //@ts-ignore
          if(setting.displayUnits == 1 && speed.activeZoneValueMt == null) speed.activeZoneValueMt = undefined;
          //@ts-ignore
          if(setting.displayUnits != 1 && speed.activeZoneValue == null) speed.activeZoneValue = undefined;
        } else if (isInclinesZoneValues) {
          setting.tempData.inclinesZoneValueError = '';
          setting.activeZoneInclinesValues.forEach(q => q.hasError=false);
          //@ts-ignore
          if(setting.displayUnits == 1 && speed.activeZoneValueMt == null) speed.activeZoneValueMt = undefined;
          //@ts-ignore
          if(setting.displayUnits != 1 && speed.activeZoneValue == null) speed.activeZoneValue = undefined;

        } else if (isLevelZoneValues) {
          setting.tempData.levelZoneValueError = '';
          setting.activeZoneLevelsValues.forEach(q => q.hasError=false);
          //@ts-ignore
          if(setting.displayUnits == 1 && speed.activeZoneValueMt == null) speed.activeZoneValueMt = undefined;
          //@ts-ignore
          if(setting.displayUnits != 1 && speed.activeZoneValue == null) speed.activeZoneValue = undefined;

        } else if (isClutchedValues) {
          setting.tempData.clutchedValueError = '';
          setting.activeZoneLevelsClutchedValues.forEach(q => q.hasError=false);
          //@ts-ignore
          if(setting.displayUnits == 1 && speed.activeZoneValueMt == null) speed.activeZoneValueMt = undefined;
          //@ts-ignore
          if(setting.displayUnits != 1 && speed.activeZoneValue == null) speed.activeZoneValue = undefined;
        } else if (isClutchlessValues) {
          setting.tempData.clutchlessValueError = '';
          setting.activeZoneLevelsClutchlessValues.forEach(q => q.hasError=false);
          //@ts-ignore
          if(setting.displayUnits == 1 && speed.activeZoneValueMt == null) speed.activeZoneValueMt = undefined;
          //@ts-ignore
          if(setting.displayUnits != 1 && speed.activeZoneValue == null) speed.activeZoneValue = undefined;
        } else {
          setting.tempData.quickValueError = '';
          setting.quickValues.forEach(q => q.hasError=false);
          //@ts-ignore
          if(setting.displayUnits == 1 && speed.quickValueMt == null) speed.quickValueMt = undefined;
          //@ts-ignore
          if(setting.displayUnits != 1 && speed.quickValue == null) speed.quickValue = undefined;
        }
      } else {
        setting.quickInclineValues.forEach(q => q.hasError=false);
        //@ts-ignore
        if(speed.quickValue == null) speed.quickValue = undefined;
        //@ts-ignore
        if(speed.quickValue == null) speed.quickValueMt = undefined;
        setting.tempData.quickInclinesValueError = '';
      }
      let filtered: any[];
      if (!isQuickInclineValues) {
        if(isQuickResistenceValues) {
          filtered = setting.quickResistanceValues.filter(t => t.quickValue != undefined);
          filtered = setting.quickResistanceValues.filter(t => t.quickValueMt != undefined);
        } else if (isSwitchCycleKeys) {
          if (setting.displayUnits == 1) {
            filtered = setting.switchCycleValues.filter(t => t.switchCycleValueMt != undefined);
          } else {
            filtered = setting.switchCycleValues.filter(t => t.switchCycleValue != undefined);
          }
        } else if (isSpeedZoneValues) {
          if (setting.displayUnits == 1) {
            filtered = setting.activeZoneSpeedValues.filter(t => t.activeZoneValueMt != undefined);
          } else {
            filtered = setting.activeZoneSpeedValues.filter(t => t.activeZoneValue != undefined);
          }
        } else if (isInclinesZoneValues) {
          if (setting.displayUnits == 1) {
            filtered = setting.activeZoneInclinesValues.filter(t => t.activeZoneValueMt != undefined);
          } else {
            filtered = setting.activeZoneInclinesValues.filter(t => t.activeZoneValue != undefined);
          }
        } else if (isLevelZoneValues) {
          if (setting.displayUnits == 1) {
            filtered = setting.activeZoneLevelsValues.filter(t => t.activeZoneValueMt != undefined);
          } else {
            filtered = setting.activeZoneLevelsValues.filter(t => t.activeZoneValue != undefined);
          }
        } else if (isClutchedValues) {
          if (setting.displayUnits == 1) {
            filtered = setting.activeZoneLevelsClutchedValues.filter(t => t.activeZoneValueMt != undefined);
          } else {
            filtered = setting.activeZoneLevelsClutchedValues.filter(t => t.activeZoneValue != undefined);
          }
        } else if (isClutchlessValues) {
          if (setting.displayUnits == 1) {
            filtered = setting.activeZoneLevelsClutchlessValues.filter(t => t.activeZoneValueMt != undefined);
          } else {
            filtered = setting.activeZoneLevelsClutchlessValues.filter(t => t.activeZoneValue != undefined);
          }
        } else {
          if (setting.displayUnits == 1) {
            filtered = setting.quickValues.filter(t => t.quickValueMt != undefined);
    
          } else {
            filtered = setting.quickValues.filter(t => t.quickValue != undefined);
          }
        }
      } else {
        filtered = setting.quickInclineValues.filter(t => t.quickValue != undefined);
        filtered = setting.quickInclineValues.filter(t => t.quickValueMt != undefined);
      }
      let isSwitchCycleKeysHasLengthError = false;
      let leftList = [];
      let rightList = [];

      if (isSwitchCycleKeys) {
        leftList = filtered.filter(item => item.switchCycleSide === 'left' && item.switchCycleValue != undefined);
        rightList = filtered.filter(item => item.switchCycleSide === 'right' && item.switchCycleValue != undefined);

        isSwitchCycleKeysHasLengthError = leftList.length < 1 || rightList.length < 1;
      }

      if(filtered.length < 2 || isSwitchCycleKeysHasLengthError) {
        if (!isQuickInclineValues) { 
          if(isQuickResistenceValues) {
            setting.quickResistanceValues.forEach(q => q.hasError=true);          
          } else if (isSwitchCycleKeys) {
            setting.switchCycleValues.forEach(q => {
              const markItemAsError = (q.switchCycleSide === 'left' && leftList.length < 1) || (q.switchCycleSide === 'right' && rightList.length < 1)
              if (markItemAsError) {
                q.hasError = true
              }
            });  
          } else if (isSpeedZoneValues) {
            setting.activeZoneSpeedValues.forEach(q => q.hasError=true);  
          } else if (isInclinesZoneValues) {
            setting.activeZoneInclinesValues.forEach(q => q.hasError=true);
          } else if (isLevelZoneValues) {
            setting.activeZoneLevelsValues.forEach(q => q.hasError=true);
          } else if (isClutchedValues) {
            setting.activeZoneLevelsClutchedValues.forEach(q => q.hasError=true);
          } else if (isClutchlessValues) {
            setting.activeZoneLevelsClutchlessValues.forEach(q => q.hasError=true);
          } else {
            setting.quickValues.forEach(q => q.hasError=true);
          }
        } else {
          setting.quickInclineValues.forEach(q => q.hasError=true);          
        }
        if (isQuickInclineValues) {
          setting.tempData.hasQuickInclinesValueError = true;
          setting.tempData.quickInclinesValueError = this.commonUtil.translateText('ATLEAST.TWO.SETTINGS.ERROR.MESSAGE');
        } else if (isSwitchCycleKeys) {
          setting.tempData.hasSwitchCycleKeysValueError = true;
          setting.tempData.switchCycleKeysValueError = this.commonUtil.translateText('ATLEAST.ONE.SETTINGS.ERROR.MESSAGE');
        } else if (isSpeedZoneValues) {
          setting.tempData.hasSpeedZoneValueError = true;
          setting.tempData.speedZoneValueError = this.commonUtil.translateText('ATLEAST.TWO.SETTINGS.ERROR.MESSAGE');
        } else if (isInclinesZoneValues) {
          setting.tempData.hasInclinesZoneValueError = true;
          setting.tempData.inclinesZoneValueError = this.commonUtil.translateText('ATLEAST.TWO.SETTINGS.ERROR.MESSAGE');
        } else if (isLevelZoneValues) {
          setting.tempData.hasLevelZoneValueError = true;
          setting.tempData.levelZoneValueError = this.commonUtil.translateText('ATLEAST.TWO.SETTINGS.ERROR.MESSAGE');
        } else if (isClutchedValues) {
          setting.tempData.hasLevelZoneClutchedError = true;
          setting.tempData.clutchedValueError = this.commonUtil.translateText('ATLEAST.TWO.SETTINGS.ERROR.MESSAGE');
        } else if (isClutchlessValues) {
          setting.tempData.hasLevelZoneClutchlessError = true;
          setting.tempData.clutchlessValueError = this.commonUtil.translateText('ATLEAST.TWO.SETTINGS.ERROR.MESSAGE');
        } else if (isQuickResistenceValues) {
          setting.tempData.hasQuickResistanceValueError = true;
          setting.tempData.quickResistanceValueError = this.commonUtil.translateText('ATLEAST.TWO.SETTINGS.ERROR.MESSAGE');
        } else {
          setting.tempData.hasQuickValueError = true;
          setting.tempData.quickValueError = this.commonUtil.translateText('ATLEAST.TWO.SETTINGS.ERROR.MESSAGE');
        }
        this.setErrorFlag(setting);
        return true;
      }
      const isMetric = setting.displayUnits == 1;
      const isZoneValues = isSpeedZoneValues || isInclinesZoneValues || isLevelZoneValues || isClutchedValues || isClutchlessValues;
      const notAscendingIndex = this.getNotAscendingIndex(!isQuickInclineValues ? isClutchlessValues ? setting.activeZoneLevelsClutchlessValues : isClutchedValues ? setting.activeZoneLevelsClutchedValues : isLevelZoneValues ? setting.activeZoneLevelsValues : isInclinesZoneValues ? setting.activeZoneInclinesValues : isSpeedZoneValues ? setting.activeZoneSpeedValues: isSwitchCycleKeys ? setting.switchCycleValues : isQuickResistenceValues ? setting.quickResistanceValues : setting.quickValues : setting.quickInclineValues, isNewModel, isMetric, isSwitchCycleKeys, isZoneValues);
      // check if values are in ascending order
      if(notAscendingIndex != -1) {
        if (!isQuickInclineValues) {
          if(isQuickResistenceValues) {
            setting.quickResistanceValues.filter((f,i) => i >= notAscendingIndex).filter(t => t.quickValue != undefined).forEach(q => q.hasError=true);
          } else if (isSwitchCycleKeys) {
            setting.switchCycleValues.filter((f,i) => i >= notAscendingIndex).filter(t => t.quickValueMt != undefined).forEach(q => q.hasError=true);
          } else if (isSpeedZoneValues) {
            if (setting.displayUnits == 1) {
              setting.activeZoneSpeedValues.filter((f,i) => i >= notAscendingIndex).filter(t => t.activeZoneValueMt != undefined).forEach(q => q.hasError=true);
            } else {
               setting.activeZoneSpeedValues.filter((f,i) => i >= notAscendingIndex).filter(t => t.activeZoneValue != undefined).forEach(q => q.hasError=true);
            }
          } else if (isInclinesZoneValues) {
            if (setting.displayUnits == 1) {
              setting.activeZoneInclinesValues.filter((f,i) => i >= notAscendingIndex).filter(t => t.activeZoneValueMt != undefined).forEach(q => q.hasError=true);
            } else {
               setting.activeZoneInclinesValues.filter((f,i) => i >= notAscendingIndex).filter(t => t.activeZoneValue != undefined).forEach(q => q.hasError=true);
            }
          } else if (isLevelZoneValues) {
            if (setting.displayUnits == 1) {
              setting.activeZoneLevelsValues.filter((f,i) => i >= notAscendingIndex).filter(t => t.activeZoneValueMt != undefined).forEach(q => q.hasError=true);
            } else {
               setting.activeZoneLevelsValues.filter((f,i) => i >= notAscendingIndex).filter(t => t.activeZoneValue != undefined).forEach(q => q.hasError=true);
            }
          } else if (isClutchedValues) {
            if (setting.displayUnits == 1) {
              setting.activeZoneLevelsClutchedValues.filter((f,i) => i >= notAscendingIndex).filter(t => t.activeZoneValueMt != undefined).forEach(q => q.hasError=true);
            } else {
               setting.activeZoneLevelsClutchedValues.filter((f,i) => i >= notAscendingIndex).filter(t => t.activeZoneValue != undefined).forEach(q => q.hasError=true);
            }
          } else if (isClutchlessValues) {
            if (setting.displayUnits == 1) {
              setting.activeZoneLevelsClutchlessValues.filter((f,i) => i >= notAscendingIndex).filter(t => t.activeZoneValueMt != undefined).forEach(q => q.hasError=true);
            } else {
               setting.activeZoneLevelsClutchlessValues.filter((f,i) => i >= notAscendingIndex).filter(t => t.activeZoneValue != undefined).forEach(q => q.hasError=true);
            }
          } else {
            if (setting.displayUnits == 1) {
              setting.quickValues.filter((f,i) => i >= notAscendingIndex).filter(t => t.quickValueMt != undefined).forEach(q => q.hasError=true);
            } else {
               setting.quickValues.filter((f,i) => i >= notAscendingIndex).filter(t => t.quickValue != undefined).forEach(q => q.hasError=true);
            }
          }
        } else {
          if (speed.quickValue) {
            setting.quickInclineValues.filter((f,i) => i >= notAscendingIndex).filter(t => t.quickValue != undefined).forEach(q => q.hasError=true);
          }
        }
        if (isQuickInclineValues) {
          setting.tempData.hasQuickInclinesValueError = true;
          setting.tempData.quickInclinesValueError = this.commonUtil.translateText('ASCENDING.ORDER.ERROR.MESSAGE');
        } else if (isSwitchCycleKeys) {
          setting.tempData.hasSwitchCycleKeysValueError = true;
          setting.tempData.switchCycleKeysValueError = this.commonUtil.translateText('ASCENDING.ORDER.ERROR.MESSAGE');
        } else if (isSpeedZoneValues) {
          setting.tempData.hasSpeedZoneValueError = true;
          setting.tempData.speedZoneValueError = this.commonUtil.translateText('ASCENDING.ORDER.ERROR.MESSAGE');
        } else if (isInclinesZoneValues) {
          setting.tempData.hasInclinesZoneValueError = true;
          setting.tempData.inclinesZoneValueError = this.commonUtil.translateText('ASCENDING.ORDER.ERROR.MESSAGE');
        } else if (isLevelZoneValues) {
          setting.tempData.hasLevelZoneValueError = true;
          setting.tempData.levelZoneValueError = this.commonUtil.translateText('ASCENDING.ORDER.ERROR.MESSAGE');
        } else if (isClutchedValues) {
          setting.tempData.hasLevelZoneClutchedError = true;
          setting.tempData.clutchedValueError = this.commonUtil.translateText('ASCENDING.ORDER.ERROR.MESSAGE');
        } else if (isClutchlessValues) {
          setting.tempData.hasLevelZoneClutchlessError = true;
          setting.tempData.clutchlessValueError = this.commonUtil.translateText('ASCENDING.ORDER.ERROR.MESSAGE');
        } else if (isQuickResistenceValues) {
          setting.tempData.hasQuickResistanceValueError = true;
          setting.tempData.quickResistanceValueError = this.commonUtil.translateText('ASCENDING.ORDER.ERROR.MESSAGE');
        } else {
          setting.tempData.hasQuickValueError = true;
          setting.tempData.quickValueError = this.commonUtil.translateText('ASCENDING.ORDER.ERROR.MESSAGE');
        }
          this.setErrorFlag(setting);
          return true;
        }
        let duplicates;
        let speedList;
        if (setting.displayUnits == 1) {
          if (isSwitchCycleKeys) {
            duplicates = filtered.filter(item => item.switchCycleValueMt != undefined).filter(f => f.switchCycleValueMt == speed.switchCycleValueMt);
            speedList = filtered.filter(item => item.switchCycleValueMt != undefined).map(item => +item.switchCycleValueMt);
          } else if (isSpeedZoneValues || isInclinesZoneValues || isLevelZoneValues || isClutchedValues || isClutchlessValues) {
            duplicates = filtered.filter(item => item.activeZoneValueMt != undefined).filter(f => f.activeZoneValueMt == speed.activeZoneValueMt);
            speedList = filtered.filter(item => item.activeZoneValueMt != undefined).map(item => +item.activeZoneValueMt);
          } else {
            duplicates = filtered.filter(f => f.quickValueMt == speed.quickValueMt);
            speedList = filtered.map(item => +item.quickValueMt);
          }
        } else {
          if (isSwitchCycleKeys) {
            duplicates = filtered.filter(item => item.switchCycleValue != undefined).filter(f => f.switchCycleValue == speed.switchCycleValue);
            speedList = filtered.filter(item => item.switchCycleValue != undefined).map(item => +item.switchCycleValue);
          } else if (isSpeedZoneValues || isInclinesZoneValues || isLevelZoneValues || isClutchedValues || isClutchlessValues) {
            duplicates = filtered.filter(item => item.activeZoneValue != undefined).filter(f => f.activeZoneValue == speed.activeZoneValue);
            speedList = filtered.filter(item => item.activeZoneValue != undefined).map(item => +item.activeZoneValue);
          } else {
            duplicates = filtered.filter(f => f.quickValue == speed.quickValue);
            speedList = filtered.map(item => +item.quickValue);
          }
      }
      const size = new Set(speedList).size;
      // check for duplicates
      if(duplicates.length > 1 || size !== speedList.length) {
        duplicates.forEach(q => q.hasError=true);
        if (isQuickInclineValues) {
          setting.tempData.hasQuickInclinesValueError = true;
          setting.tempData.quickInclinesValueError = this.commonUtil.translateText('DUPLICATE.VALUE.ERROR.MESSAGE');
        } else if (isSwitchCycleKeys) {
          setting.tempData.hasSwitchCycleKeysValueError = true;
          setting.tempData.switchCycleKeysValueError = this.commonUtil.translateText('DUPLICATE.VALUE.ERROR.MESSAGE');
        } else if (isSpeedZoneValues) {
          setting.tempData.hasSpeedZoneValueError = true;
          setting.tempData.speedZoneValueError = this.commonUtil.translateText('DUPLICATE.VALUE.ERROR.MESSAGE');
        } else if (isInclinesZoneValues) {
          setting.tempData.hasInclinesZoneValueError = true;
          setting.tempData.inclinesZoneValueError = this.commonUtil.translateText('DUPLICATE.VALUE.ERROR.MESSAGE');
        } else if (isLevelZoneValues) {
          setting.tempData.hasLevelZoneValueError = true;
          setting.tempData.levelZoneValueError = this.commonUtil.translateText('DUPLICATE.VALUE.ERROR.MESSAGE');
        } else if (isClutchedValues) {
          setting.tempData.hasLevelZoneClutchedError = true;
          setting.tempData.clutchedValueError = this.commonUtil.translateText('DUPLICATE.VALUE.ERROR.MESSAGE');
        } else if (isClutchlessValues) {
          setting.tempData.hasLevelZoneClutchlessError = true;
          setting.tempData.clutchlessValueError = this.commonUtil.translateText('DUPLICATE.VALUE.ERROR.MESSAGE');
        } else if (isQuickResistenceValues) {
          setting.tempData.hasQuickResistanceValueError = true;
          setting.tempData.quickResistanceValueError = this.commonUtil.translateText('DUPLICATE.VALUE.ERROR.MESSAGE');
        } else {
          setting.tempData.hasQuickValueError = true;
          setting.tempData.quickValueError = this.commonUtil.translateText('DUPLICATE.VALUE.ERROR.MESSAGE');
        }
        this.setErrorFlag(setting);
        return true;
      }
      const needToCheckMinMaxError = setting.displayUnits == 1 ? 
      isSpeedZoneValues || isInclinesZoneValues || isLevelZoneValues || isClutchedValues || isClutchlessValues ? speed.activeZoneValueMt != undefined : isSwitchCycleKeys ? speed.switchCycleValueMt != undefined : speed.quickValueMt != undefined :
      isSpeedZoneValues || isInclinesZoneValues || isLevelZoneValues || isClutchedValues || isClutchlessValues ? speed.activeZoneValue != undefined : isSwitchCycleKeys ?
        speed.switchCycleValue != undefined :  speed.quickValue != undefined;
      if(needToCheckMinMaxError) {
        //@ts-ignore
        if (setting.displayUnits == 1) {
          if (isSwitchCycleKeys && speed.switchCycleValueMt) {
            speed.hasError = +speed.switchCycleValueMt < +min || +speed.switchCycleValueMt > +max;
          } else if ((isSpeedZoneValues || isInclinesZoneValues || isLevelZoneValues || isClutchedValues || isClutchlessValues) && speed.activeZoneValueMt) {
            speed.hasError = +speed.activeZoneValueMt < +min || +speed.activeZoneValueMt > +max;
          } else {
            speed.hasError = +speed.quickValueMt < +min || +speed.quickValueMt > +max;
          }
        } else {
          if (isSwitchCycleKeys && speed.switchCycleValue) {
            speed.hasError = +speed.switchCycleValue < +min || +speed.switchCycleValue > +max;
          } else if ((isSpeedZoneValues || isInclinesZoneValues || isLevelZoneValues || isClutchedValues || isClutchlessValues) && speed.activeZoneValue) {
            speed.hasError = +speed.activeZoneValue < +min || +speed.activeZoneValue > +max;
          } else {
            speed.hasError = +speed.quickValue < +min || +speed.quickValue > +max;
          }
        }
        if(speed.hasError) {
          if (isQuickInclineValues) {
            setting.tempData.hasQuickInclinesValueError = true;
            setting.tempData.quickInclinesValueError = this.commonUtil.translateText('MIN.MAX.RANGE.ERROR.MESSAGE');
          } else if (isSwitchCycleKeys) {
            setting.tempData.hasSwitchCycleKeysValueError = true;
            setting.tempData.switchCycleKeysValueError = this.commonUtil.translateText('MIN.MAX.RANGE.ERROR.MESSAGE');
          } else if (isSpeedZoneValues) {
            setting.tempData.hasSpeedZoneValueError = true;
            setting.tempData.speedZoneValueError = this.commonUtil.translateText('MIN.MAX.RANGE.ERROR.MESSAGE');
          } else if (isInclinesZoneValues) {
            setting.tempData.hasInclinesZoneValueError = true;
            setting.tempData.inclinesZoneValueError = this.commonUtil.translateText('MIN.MAX.RANGE.ERROR.MESSAGE');
          } else if (isLevelZoneValues) {
            setting.tempData.hasLevelZoneValueError = true;
            setting.tempData.levelZoneValueError = this.commonUtil.translateText('MIN.MAX.RANGE.ERROR.MESSAGE');
          } else if (isClutchedValues) {
            setting.tempData.hasLevelZoneClutchedError = true;
            setting.tempData.clutchedValueError = this.commonUtil.translateText('MIN.MAX.RANGE.ERROR.MESSAGE');
          } else if (isClutchlessValues) {
            setting.tempData.hasLevelZoneClutchlessError = true;
            setting.tempData.clutchlessValueError = this.commonUtil.translateText('MIN.MAX.RANGE.ERROR.MESSAGE');
          } else if (isQuickResistenceValues) {
            setting.tempData.hasQuickResistanceValueError = true;
            setting.tempData.quickResistanceValueError = this.commonUtil.translateText('MIN.MAX.RANGE.ERROR.MESSAGE');
          } else {
            setting.tempData.hasQuickValueError = true;
            setting.tempData.quickValueError = this.commonUtil.translateText('MIN.MAX.RANGE.ERROR.MESSAGE');
          }
          this.setErrorFlag(setting);
          return true;
        }
      }
    }
    this.checkQuickSpeedOnDefaultChanges(setting);
    this.setErrorFlag(setting);
    return false;
  }

  private getNotAscendingIndex(quickValues: WorkoutSettingQuickValue[], isNewModel: boolean, isMetric?: boolean, isSwitchCycleKeys?: boolean, isZoneValues?: boolean) {
    let lastValue: number | undefined = undefined;
    for (let i = 0; i < quickValues.length; i++) {
      let quickValue = isNewModel || isMetric 
        ? isZoneValues 
          ? quickValues[i].activeZoneValueMt : isSwitchCycleKeys 
          ? quickValues[i].switchCycleValueMt : quickValues[i].quickValueMt 
        : isZoneValues 
        ? quickValues[i].activeZoneValue :  isSwitchCycleKeys 
          ? quickValues[i].switchCycleValue : quickValues[i].quickValue;
      if (quickValue) {
        quickValue = +quickValue;
        if(quickValue && isNaN(quickValue)) continue;
        if(quickValue && lastValue && +lastValue > +quickValue) return i;
        lastValue = quickValue;
      }
    }
    return -1;
  }

  private onQuickSpeedChange2(setting: WorkoutProductSettings, speed: WorkoutSettingQuickValue, isNewModel: boolean, min: number, max: number) {
    if (this.onQuickSpeedChange(setting, speed, isNewModel, min, max)) return true;
    else if (setting.tempData && this.getNotConsecutiveOrder(setting.quickValues, isNewModel, setting.displayUnits == 1) !== -1) {
      if (isNewModel) setting.tempData.quickValueNMError = this.commonUtil.translateText(
        'CONSECUTIVE.ORDER.ERROR.MESSAGE'
      );
      else setting.tempData.quickValueError = this.commonUtil.translateText(
        'CONSECUTIVE.ORDER.ERROR.MESSAGE'
      );
      speed.hasError = true;
      this.setErrorFlag(setting);
      return true;
    }
    return false;
  }

  private getNotConsecutiveOrder(quickValues: WorkoutSettingQuickValue[], isNewModel: boolean, isMetric?: boolean) {
    let lastValue: number | undefined = undefined;
    for (let i = 0; i < quickValues.length; i++) {
      let quickValue = isNewModel || isMetric ? quickValues[i].quickValueMt : quickValues[i].quickValue;
      if (quickValue) {
        quickValue = +quickValue;
        if(quickValue && isNaN(quickValue)) continue;
        if(quickValue && lastValue && +lastValue + 2 > +quickValue) return i;
        lastValue = quickValue;
      }
    }
    return -1;
  }

  checkQuickSpeedOnDefaultChanges(setting: WorkoutProductSettings, isQuickInclineValues?: boolean, levelOrSpeed?: boolean): void {
    this.showError = false;
    if (setting.tempData && levelOrSpeed) {
      setting.tempData.quickValueError = '';
      setting.tempData.hasQuickValueError = false;

    }
    if (setting.tempData && isQuickInclineValues) {
      setting.tempData.quickInclinesValueError = '';
      setting.tempData.hasQuickInclinesValueError = false;
    }
    this.minIncline = this.selectionInfo.activityId == '1' ? 0 : 1;
    const max = isQuickInclineValues ? setting.maxIncline : setting.displayUnits == 1 ? setting.maxSpeedMt : setting.maxSpeedEn
    const min = isQuickInclineValues ? this.minIncline : setting.displayUnits == 1 ? setting.minSpeedMt : setting.minSpeedEn

    if (!isQuickInclineValues && setting.quickValues && setting.quickValues.length) {
      const lastItem = setting.quickValues[setting.quickValues.length - 1];
      if (setting.displayUnits == 1) {
        lastItem.hasError = +lastItem.quickValueMt < +min || +lastItem.quickValueMt > +max;
      } else {
        lastItem.hasError = +lastItem.quickValue < +min || +lastItem.quickValue > +max;
      }

      if(lastItem.hasError) {
        if (setting.tempData) {
          setting.tempData.hasQuickValueError = true;
          setting.tempData.quickValueError = this.commonUtil.translateText('MIN.MAX.RANGE.ERROR.MESSAGE');
        }
        this.showError = true;
      }
    }

    if (isQuickInclineValues) {
      const lastItem = setting.quickInclineValues[setting.quickInclineValues.length - 1];
      lastItem.hasError = +lastItem.quickValue < +min || +lastItem.quickValue > +max;

      if(lastItem.hasError) {
        if (setting.tempData) {
          setting.tempData.hasQuickInclinesValueError = true;
          setting.tempData.quickInclinesValueError = this.commonUtil.translateText('MIN.MAX.RANGE.ERROR.MESSAGE');
        }
        this.showError = true;
      }
    }
    this.setErrorFlag(setting);
  }

  private setErrorFlag(setting: WorkoutProductSettings) {
    if(!setting?.tempData) return;
    setting.tempData.hasError = setting.tempData.hasQuickValueError ||
    setting.tempData.hasQuickInclinesValueError ||
      setting.tempData.hasSpeedZoneValueError ||
      setting.tempData.hasInclinesZoneValueError ||
      setting.tempData.hasLevelZoneValueError ||
      setting.tempData.hasLevelZoneClutchedError ||
      setting.tempData.hasLevelZoneClutchlessError ||
      setting.tempData.hasSwitchCycleKeysValueError ||
      setting.tempData.hasQuickResistanceValueError ||
      setting.tempData.maxWorkoutDurationError ||
      setting.tempData.peakMaxWorkoutDurationError ||
      setting.tempData.offpeakMaxWorkoutDurationError ||
      setting.tempData.programTimeoutError ||
      setting.tempData.pauseTimeError ||
      setting.tempData.maximumSpeedError ||
      setting.tempData.maximumSpeed2Error ||
      setting.tempData.minimumSpeedError ||
      setting.tempData.minimumSpeed2Error ||
      setting.tempData.maxInclineError ||
      setting.tempData.minInclineError ||
      setting.peakTimeList?.filter(p => p.tempData?.hasError).length > 0 ||
      setting.quickValues?.filter(p => p.hasError).length > 0 ||
      setting.quickValuesNewModel?.filter(p => p.hasError).length > 0;
  }

  private resetAllErrorFlag(setting: WorkoutProductSettings): void {
    if(!setting?.tempData) return;
    setting.tempData.hasError = setting.tempData.hasQuickValueError =
    setting.tempData.hasQuickInclinesValueError =
      setting.tempData.hasSpeedZoneValueError =
      setting.tempData.hasInclinesZoneValueError =
      setting.tempData.hasLevelZoneValueError =
      setting.tempData.hasLevelZoneClutchedError =
      setting.tempData.hasLevelZoneClutchlessError =
      setting.tempData.hasSwitchCycleKeysValueError =
      setting.tempData.hasQuickResistanceValueError =
      setting.tempData.maxWorkoutDurationError =
      setting.tempData.peakMaxWorkoutDurationError =
      setting.tempData.offpeakMaxWorkoutDurationError =
      setting.tempData.programTimeoutError =
      setting.tempData.pauseTimeError =
      setting.tempData.maximumSpeedError =
      setting.tempData.maximumSpeed2Error =
      setting.tempData.minimumSpeedError =
      setting.tempData.minimumSpeed2Error =
      setting.tempData.maxInclineError =
      setting.tempData.minInclineError = false
  }
}
